import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Box, getSize, HStack, Icon, Text} from 'platform/foundation';
import {css, styled} from 'styled-components';

import {useState} from 'react';

import {isNil} from 'ramda';

import {LevelConfiguration} from '../types';
import {SortableLevelItem} from './SortableLevelItem';

interface DrillDownLevelsConfiguratorProps {
  value: LevelConfiguration[];
  onChange: (value: LevelConfiguration[]) => void;
}

export function DrillDownLevelsConfigurator(props: DrillDownLevelsConfiguratorProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const levels = props.value ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const itemIds = levels.map((levelConfiguration) => levelConfiguration.name);

  const onDragStart = (event: DragStartEvent) => {
    setDraggedId(event.active.id.toString());
  };

  const onDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    setDraggedId(null);

    if (isNil(over) || active.id === over.id) {
      return;
    }

    const oldIndex = levels.findIndex((level) => level.name === active.id);
    const newIndex = levels.findIndex((level) => level.name === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      props.onChange?.(arrayMove(levels, oldIndex, newIndex));
    }
  };

  const draggedItem = draggedId
    ? levels.find((levelConfiguration) => levelConfiguration.name === draggedId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: ${getSize(1)};
            flex-wrap: wrap;
          `}
        >
          {levels.map((levelConfiguration) => (
            <SortableLevelItem
              key={levelConfiguration.name}
              levelConfiguration={levelConfiguration}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {draggedItem ? (
          <DragOverlayWrapper>
            <Box
              backgroundColor="palettes.neutral.40.100"
              borderRadius="small"
              paddingHorizontal={3}
            >
              <HStack height={8} align="center" spacing={1}>
                <Icon value="action/drag_indicator" size={5} />
                <Text size="small">{draggedItem.title}</Text>
              </HStack>
            </Box>
          </DragOverlayWrapper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

const DragOverlayWrapper = styled.div`
  cursor: grabbing;
`;
